import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, Card, CardBody, Container, FormGroup, Input, Label, Spinner, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import Breadcrumbs from "components/Common/Breadcrumb"
import useLogisticsOperation from "pages/EventsManagement/components/Tabs/Logistics/useLogisticsOperation"
import { actorName } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { InitialInventoryDialog, MOVEMENT_KINDS, MovementDialog, ReverseDialog } from "./StockDialogs"
import { MOVEMENT_TYPES, PERIODS, SHIPMENT_MOVEMENTS, balanceNumber, dayLabel, movementDetails, packagesLabel, periodStart, placedName, productName,
  reversedName, signed, timeLabel, todayIn, typeColor, typeLabel } from "./stockForm"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"
import "./stock.scss"

const UNCONFIRMED = "The stock could not confirm this change. Reload the page before trying again."
const movementGone = "This movement is no longer in the ledger. Reload the page."
const messages = {
  LOGISTICS_STOCK_NOT_STARTED: "The stock starts with the initial inventory. Record it first.",
  LOGISTICS_STOCK_STARTED: "The initial inventory is already recorded. Correct a count with an inventory count.",
  LOGISTICS_PRODUCT_NOT_FOUND: "This product is no longer in the catalogue. Reload the page.",
  INVALID_LOGISTICS_PLACE: "This product's places changed. Reload the page before trying again.",
  INVALID_LOGISTICS_QUANTITY: "Enter whole quantities; cases only for a product that comes in them.",
  INVALID_LOGISTICS_RECEIPT: "Enter the supplier, the document number and a day that is not after today.",
  INVALID_LOGISTICS_DESTINATION: "Choose where the goods go.",
  LOGISTICS_REASON_REQUIRED: "A reason is required.",
  INVALID_LOGISTICS_REASON: "The reason can have at most 500 characters.",
  LOGISTICS_MOVEMENT_NOT_FOUND: movementGone,
  INVALID_LOGISTICS_MOVEMENT: movementGone,
  LOGISTICS_MOVEMENT_NOT_REVERSIBLE: "Only a receipt, a manual issue or an inventory adjustment can be reversed; a shipment's movements are corrected "
    + "from its sheet.",
  LOGISTICS_MOVEMENT_REVERSED: "This movement is already reversed. Reload the page to see the ledger.",
  ERROR_PERMISSIONS: "Only an active SuperAdmin can change the stock.",
  SUPER_ADMIN_REQUIRED: "Only an active SuperAdmin can change the stock.",
  OPERATION_PAYLOAD_CONFLICT: "This request belongs to a different change. Reload before trying again.",
}
export const stockMessage = code => messages[code] || UNCONFIRMED
const SAVED = {
  recordInitialInventory: "Initial inventory recorded and logged: the stock starts now.",
  recordReceipt: "Receipt recorded and logged; the balance is updated.",
  recordManualIssue: "Manual issue recorded and logged; the balance is updated.",
  recordInventory: "Inventory count recorded and logged; the balance is updated.",
  reverseMovement: "Movement reversed and logged; the balance is updated.",
  checkBalances: "Balances checked against the ledger.",
}

// The ledger's query: the filters the page shows, the period as its first day, and the page.
const queryOf = ({ productId, type, period, page }, now) => new URLSearchParams(Object.entries({ productId, type, from: periodStart(period, now), page })
  .filter(([, value]) => value)).toString()

// "Last check: 1 Oct 2026, 11:30 by Ana Pop · 24 balances, all matching the ledger."
const checkLabel = check => {
  if (!check) return "Balances not checked yet."
  const corrected = check.corrected.length ? `${check.corrected.length} corrected from the ledger: ${check.corrected.map(({ product, place, stored, ledger }) =>
    `${placedName(product.name, place)} ${stored} → ${ledger}`).join(", ")}` : "all matching the ledger"
  return `Last check: ${timeLabel(check.at)} by ${actorName(check.actor)} · ${check.balances} balances, ${corrected}.`
}

// Logistics → Stock (US 112–121, ticket 17): the ledger of movements, filtered by product, type and period;
// the receipts, manual issues and inventory counts; the reversal of a manual movement; the check of the
// balances against the ledger and, before anything else, the initial inventory. Every command is logged.
export function LogisticsStock({ now = () => new Date() }) {
  const [filter, setFilter] = useState({ productId: "", type: "", period: "30", page: 1 })
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ loading: true })
  // The dialog open: a movement's kind, the initial inventory or a reversal (with its movement).
  const [dialog, setDialog] = useState(null)
  const [failure, setFailure] = useState("")
  const [notice, setNotice] = useState("")
  const operation = useLogisticsOperation(undefined, stockMessage)
  const query = queryOf(filter, now)

  // Another filter keeps the page's filters and buttons while its rows load.
  useEffect(() => {
    let current = true
    setState(previous => ({ answer: previous.answer, query: previous.query, loading: true }))
    get(`/admin/logistics/stock?${query}`)
      .then(response => current && setState({ answer: response?.data, query }))
      .catch(() => current && setState(previous => ({ answer: previous.answer, query: previous.query, error: "The stock could not be loaded." })))
    return () => { current = false }
  }, [query, attempt])

  const { answer } = state
  // The rows show only the filter asked for, never the previous one while it loads.
  const ready = Boolean(answer) && state.query === query
  // Every product is held in stock: the movement dialogs and the initial inventory offer them all.
  const products = answer?.products || []
  const productOf = id => products.find(product => product.id === id)
  const started = Boolean(answer?.startedAt)
  const change = field => event => setFilter({ ...filter, [field]: event.target.value, page: 1 })

  // Runs one stock command. A saved one reloads the ledger; one that cannot be confirmed reloads it too, so
  // the page shows what Events kept.
  const apply = async (action, body = {}) => {
    setFailure("")
    setNotice("")
    const result = await operation.run(action, "/admin/logistics/stock", { action, ...body })
    if (!result || result.status === "cancelled") return
    if (result.status !== "error") setAttempt(value => value + 1)
    if (result.status !== "saved") {
      setFailure(result.message || UNCONFIRMED)
      return
    }
    setDialog(null)
    setNotice(SAVED[action])
  }
  const close = () => { setDialog(null); setFailure("") }
  const open = next => { setFailure(""); setNotice(""); setDialog(next) }

  // A movement is recorded once the stock has started, for a product kept in stock.
  const movable = started && products.length > 0 && !operation.pending
  const movements = ready ? answer.movements : null
  const pages = movements ? Math.max(1, Math.ceil(movements.total / movements.limit)) : 1

  return <div className="page-content logistics-stock">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Stock" path="/logistics/stock" />
      {notice && <Alert color="success" isOpen toggle={() => setNotice("")}>{notice}</Alert>}
      {state.error && <Alert color="danger">{state.error}{" "}
        <Button color="link" className="p-0 align-baseline" onClick={() => setAttempt(value => value + 1)}>Retry</Button>
      </Alert>}
      {!answer && state.loading && <div className="text-center my-4"><Spinner color="primary" /></div>}
      {answer && <>
        <div className="logistics-stock-toolbar">
          {!started && <Button color="primary" size="sm" disabled={!products.length || operation.pending} onClick={() => open({ kind: "initial" })}>
            <i className="bx bx-list-check mr-1" aria-hidden="true" />Initial inventory</Button>}
          <Button color={started ? "primary" : "light"} size="sm" disabled={!movable} onClick={() => open({ kind: "recordReceipt" })}>
            <i className="bx bx-plus mr-1" aria-hidden="true" />Receipt</Button>
          <Button color="light" size="sm" disabled={!movable} onClick={() => open({ kind: "recordManualIssue" })}>
            <i className="bx bx-export mr-1" aria-hidden="true" />Manual issue</Button>
          <Button color="light" size="sm" disabled={!movable} onClick={() => open({ kind: "recordInventory" })}>
            <i className="bx bx-list-check mr-1" aria-hidden="true" />Inventory count</Button>
        </div>
        {started ? <p className="text-muted small">
          The balance is the sum of the movements. A movement is never edited: a mistake is reversed, by an inverse movement with a reason, then
          recorded right. Shipments write their own movements when they leave (the main shipment at Mark as shipped, a supplementary shipment
          at its validation), then at a correction, a cancellation or a return.
          Initial inventory: done on {dayLabel(answer.startedAt)}.
        </p> : <Alert color="info">
          The stock starts with the initial inventory: a physical count of every product kept in stock at Câmpina, each place apart. Until it is
          recorded, no other movement can be recorded, and the shipments validated before it change no stock.
        </Alert>}
        <Card><CardBody className="logistics-stock-check">
          <span className="small">{checkLabel(answer.check)}</span>
          <Button color="light" size="sm" disabled={operation.pending} onClick={() => apply("checkBalances")}>
            <i className="bx bx-check-double mr-1" aria-hidden="true" />Check balances</Button>
        </CardBody></Card>
        {failure && !dialog && <Alert color="danger">{failure}</Alert>}
        <Card><CardBody className="logistics-stock-filters">
          <FormGroup className="logistics-stock-filter mb-0">
            <Label for="logistics-stock-product" className="small text-muted mb-1">Product</Label>
            <Input id="logistics-stock-product" type="select" bsSize="sm" value={filter.productId} onChange={change("productId")}>
              <option value="">All products</option>
              {products.map(product => <option key={product.id} value={product.id}>{productName(product)}</option>)}
            </Input>
          </FormGroup>
          <FormGroup className="logistics-stock-filter mb-0">
            <Label for="logistics-stock-type" className="small text-muted mb-1">Movement type</Label>
            <Input id="logistics-stock-type" type="select" bsSize="sm" value={filter.type} onChange={change("type")}>
              <option value="">All types</option>
              {MOVEMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Input>
          </FormGroup>
          <FormGroup className="logistics-stock-filter mb-0">
            <Label for="logistics-stock-period" className="small text-muted mb-1">Period</Label>
            <Input id="logistics-stock-period" type="select" bsSize="sm" value={filter.period} onChange={change("period")}>
              {PERIODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Input>
          </FormGroup>
        </CardBody></Card>
        <Card tag="section" aria-label="Movements"><CardBody>
          {!movements ? <div className="text-center my-3"><Spinner size="sm" color="primary" /></div> : <>
          <div className="text-muted small mb-3">{movements.total} {movements.total === 1 ? "movement" : "movements"} · newest first
            {pages > 1 ? ` · page ${movements.page} of ${pages}` : ""}{state.loading && <Spinner size="sm" color="primary" className="ml-2" />}</div>
          {movements.rows.length ? <Table size="sm" className="logistics-stack mb-0">
            <thead><tr>
              <th>Date · author</th><th>Product</th><th>Movement</th><th className="text-right">Quantity</th><th className="text-right">Balance after</th>
              <th><span className="sr-only">Actions</span></th>
            </tr></thead>
            <tbody>{movements.rows.map(row => {
              const product = productOf(row.product.id)
              const packages = packagesLabel(product, row.quantity)
              return <tr key={row.id} className={row.reversedBy ? "logistics-inactive" : ""}>
                <td>{timeLabel(row.createdAt)}<div className="small text-muted">{actorName(row.actor)}</div></td>
                <td data-label="Product">{placedName(row.product.name, row.place)}</td>
                <td data-label="Movement">
                  <Badge color={typeColor(row.type)}>{typeLabel(row.type)}</Badge>
                  {row.reversedBy && <Badge color="soft-danger" className="ml-1">Reversed</Badge>}
                  {movementDetails(row) && <div className="small text-muted">{movementDetails(row)}</div>}
                  {/* A validated shipment's consumption, its correction, cancellation and returns, changed only through the shipment, on its
                    event's Logistics tab: the main shipment or a supplementary one, by its number. */}
                  {SHIPMENT_MOVEMENTS.includes(row.type) && row.details?.eventId && <div className="small"><Link to={`/events/${row.details.eventId}/logistics`}>
                    {`${{ main: "Main shipment", supplementary: `Supplementary #${row.details.number}` }[row.details.shipment] || "Shipment"}`
                      + ` · ${row.details.eventName || "event"}`}</Link></div>}
                  {row.reason && <div className="small text-muted"><i>“{row.reason}”</i></div>}
                </td>
                <td className="text-right" data-label="Quantity">
                  <strong className={row.quantity < 0 ? "text-danger" : row.quantity > 0 ? "text-success" : "text-muted"}>{signed(row.quantity)}</strong>
                  {packages && <div className="small text-muted">{packages}</div>}
                </td>
                <td className="text-right" data-label="Balance after">
                  <span className={row.balanceAfter < 0 ? "text-danger font-weight-medium" : ""}>{balanceNumber(row.balanceAfter)}</span></td>
                <td className="text-right" data-label="">{row.reversible && <Button color="link" size="sm" className="p-0" disabled={operation.pending}
                  aria-label={`Reverse the ${reversedName(row.type)} of ${signed(row.quantity)} ${placedName(row.product.name, row.place)}, `
                    + timeLabel(row.createdAt)} onClick={() => open({ kind: "reverse", movement: row })}>Reverse</Button>}</td>
              </tr>
            })}</tbody>
          </Table> : <p className="text-muted mb-0">No movements match these filters.</p>}
          {pages > 1 && <div className="logistics-stock-pages">
            <Button color="light" size="sm" disabled={movements.page <= 1 || state.loading} onClick={() => setFilter({ ...filter, page: movements.page - 1 })}>
              Previous page</Button>
            <Button color="light" size="sm" disabled={movements.page >= pages || state.loading} onClick={() => setFilter({ ...filter, page: movements.page + 1 })}>
              Next page</Button>
          </div>}
          </>}
        </CardBody></Card>
      </>}
    </Container>
    {dialog?.kind === "initial" && <InitialInventoryDialog products={products} pending={operation.pending} failure={failure} onSubmit={apply} onClose={close} />}
    {MOVEMENT_KINDS.includes(dialog?.kind) && <MovementDialog kind={dialog.kind} products={products} today={todayIn(now)} pending={operation.pending}
      failure={failure} onSubmit={apply} onClose={close} />}
    {dialog?.kind === "reverse" && <ReverseDialog movement={dialog.movement} product={productOf(dialog.movement.product.id)} pending={operation.pending}
      failure={failure} onSubmit={apply} onClose={close} />}
  </div>
}

export default LogisticsStock
